import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const PageContainer = styled.div`
  /* min-height: 100vh; */
  padding: 103px 40px 40px 40px;
  background: var(--background);
  animation: ${fadeInUp} 0.8s ease-out;

  margin: 0 auto;
  max-width: 1440px;

  @media (max-width: 920px) {
    padding: 91.8px 20px 20px 20px;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  width: fit-content;
  border: 2px solid var(--accent-light);
  color: var(--accent);
  padding: 12px 20px;
  border-radius: 25px;
  font-family: "Source Code Pro", monospace;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  margin-bottom: 15px;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    transform: translateY(-2px);
  }

  svg {
    font-size: 12px;
  }
`;

export const CourseHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;

  gap: 30px;
  margin-bottom: 60px;
  animation: ${scaleIn} 0.8s ease-out 0.2s both;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    /* text-align: center; */
  }

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

export const CourseImageContainer = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 20px;
  overflow: hidden;
  border: 2px solid var(--accent-light);

  transition: all 0.4s ease;
`;

export const CourseContent = styled.div`
  display: flex;
  flex-direction: column;

  gap: 25px;

  background: var(--surface);
  padding: 40px;
  border-radius: 20px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;
  height: fit-content;
  flex: 1;

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

export const CourseTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  color: var(--text-primary);
  /* margin-bottom: 15px; */
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;
export const CourseSubtitle = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 15px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.65;
  margin-top: -25px;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

export const CourseDescription = styled.div`
  font-size: 18px;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 20px;

  p {
    margin-bottom: 15px;
  }

  ul {
    margin: 15px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  .highlight {
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const CourseStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  background: var(--surface);
  border-radius: 15px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;

  svg {
    color: var(--accent);
    font-size: 16px;
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
  }
`;

export const PriceSection = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const Price = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  .original-price {
    font-size: 18px;
    color: var(--text-muted);
    text-decoration: line-through;
    font-weight: 500;
  }

  .current-price {
    font-size: 28px;
    font-weight: 700;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    .current-price {
      font-size: 24px;
    }
  }
`;

export const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px 35px;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-transform: uppercase;
  font-size: 14px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.3s ease;
  box-shadow: var(--shadow-soft);
  white-space: nowrap;

  &:hover {
    transform: translateY(-3px);
    background: var(--accent-dark);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  &.added {
    background: var(--success-color, #10b981);

    &:hover {
      background: var(--success-color-dark, #059669);
    }
  }

  svg {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

export const CourseDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 20px;
  }
`;

export const DetailSection = styled.div`
  background: var(--surface);
  padding: 40px;
  border-radius: 20px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 25px;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const WhatYouLearn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const LearningItem = styled.div`
  display: flex;
  align-items: flex-start;

  gap: 15px;

  svg {
    color: var(--accent);
    font-size: 16px;
    margin-top: 4.5px;
    flex-shrink: 0;
  }

  span {
    font-size: 16px;
    line-height: 1.6;
    color: var(--text-secondary);
  }
`;

export const CourseIncludes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const IncludeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  svg {
    color: var(--accent);
    font-size: 16px;
    flex-shrink: 0;
  }

  span {
    font-size: 16px;
    color: var(--text-secondary);
  }
`;

export const InstructorSection = styled.div`
  grid-column: 1 / -1;
  background: var(--surface);
  padding: 40px;
  border-radius: 20px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

export const InstructorInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 25px;

  .instructor-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--gradient-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      font-size: 32px;
      color: var(--surface);
    }
  }

  .instructor-details {
    flex: 1;

    h3 {
      font-size: 22px;
      font-weight: 700;
      color: var(--text-primary);
      margin-bottom: 10px;
    }

    p {
      font-size: 16px;
      line-height: 1.7;
      color: var(--text-secondary);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: 18px;
  color: var(--text-secondary);
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  gap: 20px;

  h2 {
    font-size: 28px;
    color: var(--text-primary);
    margin-bottom: 10px;
  }

  p {
    font-size: 16px;
    color: var(--text-secondary);
    margin-bottom: 20px;
  }
`;
